import { Component } from '@theme/component';

const TAB_STORAGE_KEY = 'maryluna-tab';

class MarylunaHeaderComponent extends Component {
  #searchController = null;
  #searchTimer = 0;
  #resizeObserver = null;
  #countdownTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#syncTabs();
    this.#startCountdown();
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);

    const sticky = this.querySelector('#header-component');
    if (sticky) {
      this.#resizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--header-height', `${sticky.offsetHeight}px`);
        document.body.style.setProperty('--header-group-height', `${this.offsetHeight}px`);
      });
      this.#resizeObserver.observe(sticky);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#searchTimer);
    clearInterval(this.#countdownTimer);
    this.#searchController?.abort();
    if (this.querySelector('dialog[open]')) document.documentElement.removeAttribute('scroll-lock');
  }

  #startCountdown() {
    const countdown = this.querySelector('[data-countdown]');
    const end = Date.parse(countdown?.dataset.countdown ?? '');
    if (!countdown) return;
    if (Number.isNaN(end)) {
      countdown.hidden = true;
      return;
    }
    const value = countdown.querySelector('[data-countdown-value]');
    const pad = (number) => String(number).padStart(2, '0');
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      value.textContent = `${pad(Math.floor(left / 86400))}d ${pad(Math.floor((left % 86400) / 3600))}h ${pad(Math.floor((left % 3600) / 60))}m ${pad(left % 60)}s`;
      countdown.hidden = left === 0;
      if (left === 0) clearInterval(this.#countdownTimer);
    };
    tick();
    if (countdown.hidden) return;
    this.#countdownTimer = setInterval(tick, 1000);
  }

  selectTab(event) {
    const index = event.target.dataset.tab;
    this.dataset.activeTab = index;
    this.#syncTabs();
    this.closeMenus();
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, index);
    } catch {}
  }

  #syncTabs() {
    for (const tab of this.querySelectorAll('[data-tab]')) {
      const active = String(tab.dataset.tab === this.dataset.activeTab);
      tab.setAttribute(tab.tagName === 'A' ? 'aria-current' : 'aria-pressed', active);
    }
  }

  rememberTab(event) {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, event.target.dataset.tab);
    } catch {}
  }

  toggleMenu(event) {
    const button = event.target;
    const isOpen = button.getAttribute('aria-expanded') === 'true';
    this.closeMenus();
    if (isOpen) return;
    button.setAttribute('aria-expanded', 'true');
    document.getElementById(button.getAttribute('aria-controls'))?.removeAttribute('hidden');
  }

  closeMenus() {
    for (const button of this.querySelectorAll('.ml-nav__button[aria-expanded="true"]')) {
      button.setAttribute('aria-expanded', 'false');
      document.getElementById(button.getAttribute('aria-controls'))?.setAttribute('hidden', '');
    }
  }

  openDialog(event) {
    const dialog = this.querySelector(`#${event.target.dataset.dialog}`);
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    this.closeMenus();
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.addEventListener('close', () => document.documentElement.removeAttribute('scroll-lock'), { once: true });
    dialog.querySelector('[data-autofocus]')?.focus();
  }

  closeDialog(event) {
    event.target.closest('dialog')?.close();
  }

  onDialogClick(event) {
    if (event.composedPath()[0] === event.target) event.target.close();
  }

  focusSearch(event) {
    const input = event.target;
    input.placeholder = input.dataset.focusPlaceholder;
    input.closest('.ml-search').classList.add('is-focused');
  }

  blurSearch(event) {
    const input = event.target;
    input.placeholder = input.dataset.placeholder;
    if (!input.value) input.closest('.ml-search').classList.remove('is-focused');
  }

  clearSearch(event) {
    const form = event.target.closest('.ml-search');
    const input = form.querySelector('input[name="q"]');
    input.value = '';
    this.#hideResults(form);
    input.focus();
  }

  search(event) {
    const input = event.target;
    const form = input.closest('.ml-search');
    clearTimeout(this.#searchTimer);
    const query = input.value.trim();
    if (!query) {
      this.#searchController?.abort();
      this.#hideResults(form);
      return;
    }
    this.#searchTimer = setTimeout(() => this.#fetchSuggestions(form, query), 200);
  }

  async #fetchSuggestions(form, query) {
    this.#searchController?.abort();
    this.#searchController = new AbortController();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
    });

    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest.json?${params}`, {
        signal: this.#searchController.signal,
      });
      if (!response.ok) throw new Error(response.statusText);
      const { resources } = await response.json();
      this.#renderResults(form, query, resources.results);
    } catch (error) {
      if (error.name !== 'AbortError') this.#hideResults(form);
    }
  }

  #renderResults(form, query, results) {
    const container = form.querySelector('.ml-search__results');
    const template = this.querySelector('#ml-search-results-template');
    const fragment = template.content.cloneNode(true);
    const suggestions = fragment.querySelector('[data-suggestions]');
    const products = fragment.querySelector('[data-products]');
    const searchUrl = `${form.action}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;

    const terms = [query, ...results.queries.map((item) => item.text).filter((text) => text.toLowerCase() !== query.toLowerCase())];
    for (const [index, term] of terms.slice(0, 5).entries()) {
      const link = document.createElement('a');
      link.href = `${form.action}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      link.textContent = term;
      if (index === 0) link.classList.add('is-current');
      suggestions.append(link);
    }

    for (const product of results.products) {
      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'ml-search__product';
      if (product.image) {
        const image = document.createElement('img');
        const src = new URL(product.image, window.location.origin);
        src.searchParams.set('width', '160');
        image.src = src.toString();
        image.alt = '';
        image.width = 80;
        image.height = 116;
        image.loading = 'lazy';
        link.append(image);
      }
      const title = document.createElement('span');
      title.textContent = product.title;
      link.append(title);
      products.append(link);
    }

    fragment.querySelector('[data-view-all]').href = searchUrl;
    fragment.querySelector('[data-products-block]').hidden = results.products.length === 0;
    container.replaceChildren(fragment);
    container.hidden = false;
    form.querySelector('input[name="q"]').setAttribute('aria-expanded', 'true');
  }

  #hideResults(form) {
    const container = form.querySelector('.ml-search__results');
    container.hidden = true;
    container.replaceChildren();
    form.querySelector('input[name="q"]').setAttribute('aria-expanded', 'false');
  }

  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.ml-nav__item')) this.closeMenus();
    for (const dropdown of this.querySelectorAll('details[data-dropdown][open]')) {
      if (!dropdown.contains(target)) dropdown.removeAttribute('open');
    }
    for (const form of this.querySelectorAll('.ml-search')) {
      if (!form.contains(target)) form.querySelector('.ml-search__results').hidden = true;
    }
  };

  #onFocusOut = (event) => {
    const item = event.target instanceof Element ? event.target.closest('.ml-nav__item') : null;
    if (item && !item.contains(event.relatedTarget)) this.closeMenus();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('.ml-nav__button[aria-expanded="true"]');
    const refocus = open?.closest('.ml-nav__item')?.contains(document.activeElement);
    this.closeMenus();
    if (refocus) open.focus();
    for (const dropdown of this.querySelectorAll('details[data-dropdown][open]')) dropdown.removeAttribute('open');
    for (const results of this.querySelectorAll('.ml-search__results')) results.hidden = true;
  };
}

if (!customElements.get('maryluna-header-component')) {
  customElements.define('maryluna-header-component', MarylunaHeaderComponent);
}
